import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectOption } from '../ui/select';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Users, Plus, Trash2, RefreshCw } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';

const DevelopersStep = ({ formData, setFormData, calculateAmounts }) => {
  const [newDeveloper, setNewDeveloper] = useState({
    name: '',
    amount: 0,
    payment_mode: 'percentage',
    percentage: 0,
    withholding_tax_percentage: 3,
    withholding_tax_amount: 0,
    net_amount: 0,
    paid: false,
    payment_date: '',
    channel: 'bank_transfer'
  });

  const addDeveloperPayment = () => {
    if (!newDeveloper.name || (!newDeveloper.percentage && !newDeveloper.amount)) {
      return;
    }

    // คำนวณจำนวนเงินตาม payment_mode
    let calculatedAmount = 0;
    if (newDeveloper.payment_mode === 'percentage') {
      // คำนวณจากเปอร์เซ็นต์ของเงินที่เหลือสำหรับ Developer
      const amounts = calculateAmounts();
      const remainingForDev = amounts.remainingBeforeDev || 0;
      calculatedAmount = (remainingForDev * newDeveloper.percentage) / 100;
    } else {
      calculatedAmount = newDeveloper.amount;
    }

    // คำนวณ WHT
    const withholdingTaxAmount = (calculatedAmount * newDeveloper.withholding_tax_percentage) / 100;
    
    // คำนวณจำนวนเงินหลังหัก WHT
    const netAmount = calculatedAmount - withholdingTaxAmount;

    const developer = {
      ...newDeveloper,
      amount: calculatedAmount,
      withholding_tax_amount: withholdingTaxAmount,
      net_amount: netAmount,
      type: newDeveloper.payment_mode, // Set type based on payment_mode
      id: Date.now()
    };

    setFormData(prev => ({
      ...prev,
      developer_payments: [...(prev.developer_payments || []), developer]
    }));

    setNewDeveloper({
      name: '',
      amount: 0,
      payment_mode: 'percentage',
      percentage: 0,
      withholding_tax_percentage: 3,
      withholding_tax_amount: 0,
      net_amount: 0,
      paid: false,
      payment_date: '',
      channel: 'bank_transfer'
    });
  };

  const removeDeveloperPayment = (id) => {
    setFormData(prev => ({
      ...prev,
      developer_payments: (prev.developer_payments || []).filter(dev => dev.id !== id)
    }));
  };

  const updateDeveloperPayment = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      developer_payments: (prev.developer_payments || []).map(dev =>
        dev.id === id ? { ...dev, [field]: value } : dev
      )
    }));
  };

  const recalculateDeveloperPayments = () => {
    if (!formData.developer_payments || formData.developer_payments.length === 0) {
      return;
    }

    const amounts = calculateAmounts();
    const remainingForDev = amounts.remainingBeforeDev || 0;

    setFormData(prev => ({
      ...prev,
      developer_payments: (prev.developer_payments || []).map(dev => {
        let calculatedAmount = 0;
        
        if (dev.payment_mode === 'percentage') {
          // คำนวณจากเปอร์เซ็นต์ของเงินที่เหลือสำหรับ Developer
          calculatedAmount = (remainingForDev * dev.percentage) / 100;
        } else {
          calculatedAmount = dev.amount;
        }

        // คำนวณ WHT
        const withholdingTaxAmount = (calculatedAmount * dev.withholding_tax_percentage) / 100;
        
        // คำนวณจำนวนเงินหลังหัก WHT
        const netAmount = calculatedAmount - withholdingTaxAmount;

        return {
          ...dev,
          amount: calculatedAmount,
          withholding_tax_amount: withholdingTaxAmount,
          net_amount: netAmount
        };
      })
    }));
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-indigo-100 rounded-full mb-3">
          <Users className="h-6 w-6 text-indigo-600" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-1">Developer</h2>
        <p className="text-muted-foreground text-sm">จัดการค่าจ้างและรายละเอียด Developer</p>
      </div>

      {/* Add New Developer */}
      <Card className="border-2 border-indigo-200 hover:border-indigo-300 transition-colors">
        <CardHeader className="bg-indigo-50 py-3">
          <CardTitle className="flex items-center gap-2 text-indigo-800">
            <Plus className="h-5 w-5" />
            เพิ่ม Developer ใหม่
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dev_name">ชื่อ Developer</Label>
              <Input
                id="dev_name"
                placeholder="กรอกชื่อ Developer"
                value={newDeveloper.name}
                onChange={(e) => setNewDeveloper(prev => ({ ...prev, name: e.target.value }))}
                className="h-10"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="dev_payment_mode">ประเภทการจ่าย</Label>
              <Select
                value={newDeveloper.payment_mode}
                onChange={(e) => setNewDeveloper(prev => ({ ...prev, payment_mode: e.target.value }))}
              >
                <SelectOption value="percentage">เปอร์เซ็นต์</SelectOption>
                <SelectOption value="amount">จำนวนเงิน</SelectOption>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dev_value">
                {newDeveloper.payment_mode === 'percentage' ? 'เปอร์เซ็นต์ (%)' : 'จำนวนเงิน'}
              </Label>
              <Input
                id="dev_value"
                type="number"
                placeholder={newDeveloper.payment_mode === 'percentage' ? '50' : '100000'}
                value={newDeveloper.payment_mode === 'percentage' ? newDeveloper.percentage : newDeveloper.amount}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  if (newDeveloper.payment_mode === 'percentage') {
                    setNewDeveloper(prev => ({ ...prev, percentage: value }));
                  } else {
                    setNewDeveloper(prev => ({ ...prev, amount: value }));
                  }
                }}
                className="h-10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dev_wht_percentage">WHT (%)</Label>
              <Input
                id="dev_wht_percentage"
                type="number"
                placeholder="3"
                value={newDeveloper.withholding_tax_percentage}
                onChange={(e) => {
                  const percentage = parseFloat(e.target.value) || 0;
                  const amount = newDeveloper.payment_mode === 'percentage' 
                    ? (newDeveloper.percentage * percentage) / 100
                    : (newDeveloper.amount * percentage) / 100;
                  setNewDeveloper(prev => ({ 
                    ...prev, 
                    withholding_tax_percentage: percentage,
                    withholding_tax_amount: amount,
                    net_amount: (newDeveloper.payment_mode === 'percentage' ? newDeveloper.percentage : newDeveloper.amount) - amount
                  }));
                }}
                className="h-10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dev_payment_date">วันที่จ่าย</Label>
              <Input
                id="dev_payment_date"
                type="date"
                value={newDeveloper.payment_date}
                onChange={(e) => setNewDeveloper(prev => ({ ...prev, payment_date: e.target.value }))}
                className="h-10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dev_channel">ช่องทางการจ่าย</Label>
              <Select
                value={newDeveloper.channel}
                onChange={(e) => setNewDeveloper(prev => ({ ...prev, channel: e.target.value }))}
              >
                <SelectOption value="bank_transfer">โอนเงิน</SelectOption>
                <SelectOption value="cash">เงินสด</SelectOption>
                <SelectOption value="check">เช็ค</SelectOption>
              </Select>
            </div>
          </div>

          <div className="mt-4">
            <Button
              onClick={addDeveloperPayment}
              disabled={!newDeveloper.name || (!newDeveloper.percentage && !newDeveloper.amount)}
              className="w-full h-10"
            >
              <Plus className="h-4 w-4 mr-2" />
              เพิ่ม Developer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Developers Table */}
      <Card className="border-2 border-blue-200 hover:border-blue-300 transition-colors">
        <CardHeader className="bg-blue-50 py-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Users className="h-5 w-5" />
              รายการ Developer
            </CardTitle>
            {formData.developer_payments && formData.developer_payments.length > 0 && (
              <Button
                onClick={recalculateDeveloperPayments}
                variant="outline"
                size="sm"
                className="text-blue-600 border-blue-300 hover:bg-blue-100"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                คำนวณใหม่
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {formData.developer_payments && formData.developer_payments.length > 0 ? (
            <div className="overflow-x-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-32 text-center">รายการ</TableHead>
                    <TableHead className="w-28 text-right">ก่อน WHT</TableHead>
                    <TableHead className="w-32 text-right">หลัง WHT</TableHead>
                    <TableHead className="w-24 text-center">สถานะ</TableHead>
                    <TableHead className="w-28">วันที่ชำระ</TableHead>
                    <TableHead className="w-24 text-center">ช่องทาง</TableHead>
                    <TableHead className="w-16 text-center">จัดการ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(formData.developer_payments || []).map((dev, index) => (
                    <TableRow key={dev.id || index}>
                      <TableCell className="text-center">
                        <div>
                          <div className="font-medium text-xs">{dev.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {dev.payment_mode === 'percentage' ? `${dev.percentage}%` : formatCurrency(dev.amount)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        {formatCurrency(dev.amount || 0)}
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        <div>
                          <div className="font-medium">{formatCurrency(dev.net_amount || dev.amount || 0)}</div>
                          <div className="text-xs text-muted-foreground">
                            WHT {dev.withholding_tax_percentage}%: {formatCurrency(dev.withholding_tax_amount || 0)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={dev.paid ? 'paid' : 'pending'}
                          onChange={(e) => updateDeveloperPayment(dev.id, 'paid', e.target.value === 'paid')}
                          className="text-xs"
                        >
                          <SelectOption value="pending">รอจ่าย</SelectOption>
                          <SelectOption value="paid">จ่ายแล้ว</SelectOption>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="date"
                          value={dev.payment_date || ''}
                          onChange={(e) => updateDeveloperPayment(dev.id, 'payment_date', e.target.value)}
                          className="h-6 text-xs w-full"
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={dev.channel || 'bank_transfer'}
                          onChange={(e) => updateDeveloperPayment(dev.id, 'channel', e.target.value)}
                          className="text-xs"
                        >
                          <SelectOption value="bank_transfer">โอนเงิน</SelectOption>
                          <SelectOption value="cash">เงินสด</SelectOption>
                          <SelectOption value="check">เช็ค</SelectOption>
                        </Select>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => removeDeveloperPayment(dev.id)}
                          className="h-6 w-6 p-0"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Summary Row */}
                  {formData.developer_payments && formData.developer_payments.length > 0 && (
                    <TableRow className="bg-gray-50 border-t-2 border-gray-200">
                      <TableCell className="text-center font-semibold text-gray-800">
                        รวม
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-800">
                        {formatCurrency(
                          (formData.developer_payments || []).reduce((sum, dev) => sum + (dev.amount || 0), 0)
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-800">
                        <div>
                          <div>{formatCurrency(
                            (formData.developer_payments || []).reduce((sum, dev) => sum + (dev.net_amount || 0), 0)
                          )}</div>
                          <div className="text-xs text-gray-600">
                            WHT: {formatCurrency(
                              (formData.developer_payments || []).reduce((sum, dev) => sum + (dev.withholding_tax_amount || 0), 0)
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="text-xs text-gray-600">
                          {(formData.developer_payments || []).filter(dev => dev.paid).length} / {(formData.developer_payments || []).length}
                        </div>
                      </TableCell>
                      <TableCell colSpan="3" className="text-center text-xs text-gray-600">
                        {(formData.developer_payments || []).length} คน
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">ยังไม่มี Developer</p>
              <p className="text-sm text-muted-foreground">กรุณาเพิ่ม Developer ใหม่</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DevelopersStep;
